import type { CampaignInsertionOrder, InsertionOrderKey, InsertionOrderRule } from 'c-sdk';

/**
 * UL-7912 -- the Insertion Order Selection dialog's ticks, turned into what Apply changes.
 */

/**
 * Which insertion orders were ticked or un-ticked since the dialog opened, given the picks it
 * opened with. A key is turned back into an insertion order from the rows seen while open, or, for
 * a pick on a page never visited, from the pick rule itself. A key neither explains is dropped.
 */
export const selectionChanges = (
    ticked: Set<string>,
    picks: Map<string, InsertionOrderRule>,
    seen: Map<string, InsertionOrderKey>,
): { newlyTicked: InsertionOrderKey[]; newlyUnticked: InsertionOrderKey[] } => {
    const toKey = (key: string): InsertionOrderKey | null => {
        const fromSeen = seen.get(key);
        if (fromSeen) return fromSeen;

        const pick = picks.get(key);
        if (pick?.customer == null || pick.conditions.length === 0) return null;

        return { customer: pick.customer, insertion_order_id: pick.conditions[0].identifier };
    };
    const known = (io: InsertionOrderKey | null): io is InsertionOrderKey => io !== null;

    return {
        newlyTicked: [...ticked]
            .filter(key => !picks.has(key))
            .map(toKey)
            .filter(known),
        newlyUnticked: [...picks.keys()]
            .filter(key => !ticked.has(key))
            .map(toKey)
            .filter(known),
    };
};

/**
 * A row's checkbox: ticked when the user has it ticked, indeterminate when it is attached only by a
 * broader rule (shown, not owned: un-ticking can never remove it), otherwise unticked.
 */
export const selectionRowState = (
    key: string,
    ticked: Set<string>,
    picks: Map<string, unknown>,
    attached: Map<string, unknown>,
): 'ticked' | 'attachedByRule' | 'unticked' => {
    if (ticked.has(key)) return 'ticked';
    if (!picks.has(key) && attached.has(key)) return 'attachedByRule';
    return 'unticked';
};

/** The ticked table rows the schedule ＋/－ buttons act on; keys no longer attached are dropped. */
export const selectedInsertionOrderKeys = (
    selectedKeys: string[],
    byKey: Map<string, CampaignInsertionOrder>,
): InsertionOrderKey[] =>
    selectedKeys
        .map(key => byKey.get(key))
        .filter((io): io is CampaignInsertionOrder => io !== undefined)
        .map(io => ({ customer: io.customer, insertion_order_id: io.insertion_order_id }));
